/**
 * The chrome every /me/* page renders inside: the page's title and the name of
 * the teacher looking at it.
 *
 * Its own file so `TeacherDashboards` holds only the route table — the
 * one-component-per-file rule this pair had been breaking since before the
 * mode cutover.
 */
import { Outlet, useLocation } from 'react-router-dom';

import { useCurrentUser } from '../../../contexts/CurrentUserContext';
import { useViewport } from '../../../hooks/useViewport';
import { DashboardsPage } from '../DashboardsPage';
import { DashboardPageHeader } from '../primitives/DashboardPageHeader';

import RegionSchedulePicker from './components/RegionSchedulePicker';

/** Per-bucket titles from the design's teacher-*.jsx `title` props. */
function titleFor(bucket: string, firstName: string): string {
  const titles: Record<string, string> = {
    schedule: `Hi, ${firstName}`,
    'region-schedule': 'Region schedule',
    supplies: 'My prep & supplies',
    resources: `${firstName}'s resources`,
    availability: 'Availability',
    shifts: 'Open shifts',
    requests: 'My requests',
    history: 'My work history',
    pay: 'My pay',
  };
  return titles[bucket] ?? titles.schedule;
}

export function TeacherShell() {
  // Display name comes from CurrentUserContext (already fetched at app boot)
  // so the chrome doesn't depend on the overview-query envelope landing first.
  const { currentUser } = useCurrentUser();
  const { pathname } = useLocation();
  const { isPhone } = useViewport();
  const firstName = currentUser?.firstName?.trim() || 'Teacher';
  const fullName =
    [currentUser?.firstName?.trim(), currentUser?.lastName?.trim()].filter(Boolean).join(' ') ||
    firstName;
  const bucket = pathname.match(/^\/me\/([^/]+)/)?.[1] ?? 'schedule';

  return (
    <DashboardsPage
      header={
        <DashboardPageHeader
          title={titleFor(bucket, firstName)}
          isPhone={isPhone}
          scope={
            bucket === 'region-schedule' ? (
              <RegionSchedulePicker />
            ) : (
              <span style={{ color: 'var(--nb-subtle)' }}>{fullName}</span>
            )
          }
        />
      }
    >
      <Outlet />
    </DashboardsPage>
  );
}
